import moment from "moment";
import React from "react";

import Callout from "../components/Callout";
import { ApproximateNextReviewSessionStatus } from "../reviewSchedule";
import { EssayProgress } from "../util/getProgressByEssay";
import { OverlayCoachMarkID, SimpleCoachMarkID } from "./coachMarkListing";
import {
  getIntervalLabelsForSchedule,
  SpacedRepetitionSchedule,
} from "../spacedRepetition";

type CoachMarkContents = {
  [ID in SimpleCoachMarkID | OverlayCoachMarkID]: ID extends SimpleCoachMarkID
    ? (args: { schedule: SpacedRepetitionSchedule }) => string
    : OverlayCoachMarkSpec;
};

export const coachMarkCelebrationToken = "::CELEBRATION:: ";

type OverlayCoachMarkSpec = (args: {
  isDesktop: boolean;
  hasApplicationPrompts: boolean;
  nodeLocations: number[][];
  nodeWidth: number;
  nodeHeight: number;
  progress: EssayProgress;
  approximateNextReviewSessionStatus: ApproximateNextReviewSessionStatus;
  schedule: SpacedRepetitionSchedule;
}) => React.ReactElement;

const coachMarkContents: CoachMarkContents = {
  firstRetryInEssay: () => `**It’s fine to forget.**

We’ll try that question again in the next set. Our system tunes each question’s review schedule to help you remember everything.`,

  firstReviewAreaComplete: ({
    isDesktop,
    hasApplicationPrompts,
    nodeLocations,
    nodeWidth,
    nodeHeight,
    schedule,
  }) => (
    <>
      <Callout x={isDesktop ? -100 : 0} y={isDesktop ? -56 : -15} width={220}>
        {`**Quantum Country helps you remember${
          hasApplicationPrompts ? " and apply" : ""
        } what you
        read** by answering quick questions like you just did.`}
      </Callout>
      <Callout
        x={isDesktop ? -100 : 0}
        y={isDesktop ? 50 : 93}
        tail={{
          x: nodeLocations[0][0] + nodeWidth / 2,
          y: nodeLocations[0][1] - 8,
        }}
        width={220}
      >
        You'll immediately test yourself with in-text questions.
      </Callout>
      <Callout
        x={isDesktop ? 40 : 50}
        y={isDesktop ? 120 : 165}
        tail={{
          x: nodeLocations[1][0] + nodeWidth / 2,
          y: nodeLocations[1][1] - 8,
        }}
        width={155}
      >
        {`We'll send you a quick review to reinforce what you've learned after a
        break of ${getIntervalLabelsForSchedule(schedule)[1]}.`}
      </Callout>
      <Callout
        x={isDesktop ? nodeLocations[3][0] - 270 / 2 : nodeLocations[3][0]}
        y={340}
        tail={{
          x: nodeLocations[3][0] + nodeWidth / 2,
          y: nodeLocations[3][1] + nodeHeight - 24,
        }}
        width={190}
      >
        Once you answer a prompt correctly, we'll help you build fluency with
        longer break, working up to many months between reviews.
      </Callout>
    </>
  ),

  firstLevelComplete: ({
    isDesktop,
    hasApplicationPrompts,
    nodeLocations,
    nodeWidth,
    nodeHeight,
    progress,
    approximateNextReviewSessionStatus,
    schedule,
  }) => {
    const when = approximateNextReviewSessionStatus.approximateTimestampMillis
      ? moment(
          approximateNextReviewSessionStatus.approximateTimestampMillis,
        ).fromNow()
      : "soon";

    if (progress[1].collectedCardCount === 0) {
      // Hasn't done a review session yet.
      return (
        <>
          <Callout
            x={isDesktop ? -100 : -8}
            y={isDesktop ? -56 : -15}
            width={225}
            isEmphasized
            isCelebration
          >
            **You’ve now been able to answer every in-text question in this
            essay!**
          </Callout>
          <Callout
            x={isDesktop ? -100 : -8}
            y={isDesktop ? 23 : 64}
            width={225}
          >
            {`Quantum Country can help you easily remember${
              hasApplicationPrompts ? " and apply" : ""
            } those details not just
            for a few minutes, but indefinitely.`}
          </Callout>
          <Callout
            x={isDesktop ? -100 : -8}
            y={isDesktop ? 121 : 162}
            width={220}
          >
            {`We’ll send you a link to reinforce your grasp of the material with a quick review ${when}.`}
          </Callout>
          {/*<Callout*/}
          {/*  x={isDesktop ? nodeLocations[1][0] : nodeLocations[2][0] + 20}*/}
          {/*  y={*/}
          {/*    isDesktop*/}
          {/*      ? nodeLocations[1][1] + nodeHeight + 32*/}
          {/*      : nodeLocations[1][1] + nodeHeight / 2 - 22*/}
          {/*  }*/}
          {/*  tail={{*/}
          {/*    x: isDesktop*/}
          {/*      ? nodeLocations[1][0] + nodeWidth / 2*/}
          {/*      : nodeLocations[1][0] + nodeWidth / 2 + 16,*/}
          {/*    y: isDesktop*/}
          {/*      ? nodeLocations[1][1] + nodeHeight / 2 + 32*/}
          {/*      : nodeLocations[1][1] + nodeHeight / 2 - 6,*/}
          {/*  }}*/}
          {/*  width={170}*/}
          {/*>*/}
          {/*  {`Unlocks ${when}`}*/}
          {/*</Callout>*/}
        </>
      );
    } else {
      // TODO: Something more clever...
      const predictedRemainingSessions = Math.ceil(
        (progress[1].totalCardCount - progress[1].collectedCardCount) / 35.0,
      );

      // Has already done at least one review session.
      return (
        <>
          <Callout
            x={isDesktop ? -100 : -8}
            y={isDesktop ? 50 : -15}
            width={225}
            isCelebration
            isEmphasized
          >
            **You’ve now been able to answer every in-text question in this
            essay!**
          </Callout>
          <Callout
            x={isDesktop ? -100 : -8}
            y={isDesktop ? 142 : 154}
            width={isDesktop ? 220 : 200}
            tail={{
              x: nodeLocations[1][0] + nodeWidth / 2,
              y: nodeLocations[1][1] - 12,
            }}
          >
            {`Now you'll be able to complete the next level: remembering
            all the answers after ${
              getIntervalLabelsForSchedule(schedule)[1]
            } away.`}
          </Callout>
          <Callout
            x={isDesktop ? nodeLocations[1][0] : nodeLocations[2][0] + 20}
            y={
              isDesktop
                ? nodeLocations[1][1] + nodeHeight + 32
                : nodeLocations[1][1] + nodeHeight / 2 - 22
            }
            tail={{
              x: isDesktop
                ? nodeLocations[1][0] + nodeWidth / 2
                : nodeLocations[1][0] + nodeWidth / 2 + 24,
              y: isDesktop
                ? nodeLocations[1][1] + nodeHeight / 2 + 32
                : nodeLocations[1][1] + nodeHeight / 2 - 6,
            }}
            width={170}
          >
            {`Readers similar to you usually finish this level in about **${predictedRemainingSessions} more sessions.**`}
          </Callout>
        </>
      );
    }
  },

  answeredFirstSecondLevelQuestion: ({ schedule }) => {
    const sequence = getIntervalLabelsForSchedule(schedule);
    const firstIntervalLabel = sequence[1].replace(" ", " ");
    return `::CELEBRATION:: You were able to answer that question after **${firstIntervalLabel}**. Now you won’t need to review it for **${sequence[2]}**.
  
  Each time you correctly answer a question, you’ll retain that knowledge longer. You’ll eventually be able to draw on all this material after months away.`;
  },

  firstRetryInReview: () => `**No problem: it’s normal to forget.**

We’ll come back to that question at the end of this review session.`,
};

export default coachMarkContents;
